import { useQuery } from "@tanstack/react-query";
import * as R from "ramda";

import { fetchDistinctProtocols } from "../queries/artifacts";
import type { ConsolidatedLensItem } from "../../services/lensService";
import { getLensContent, getLensData } from "../../services/lensService";
import { TimeRange } from "../../utils/timerange";
import { extractTitleFromMarkdown } from "../../features/account-analysis/utils/markdown";
import { useAuth } from "../../features/auth/AuthContext";

export interface LensProtocol {
  name: string;
  fullPath: string;
}

/**
 * Strips common suffixes like "Lens" or "Protocol" from the title
 */
const stripTitleSuffixes = (title: string): string => {
  return title
    .replace(/\s+(Lens|Protocol)$/i, "")
    .trim();
};

interface UseLensProtocolsParams {
  timeRange: TimeRange;
  permittedEmails: string[] | null;
}

/**
 * Where we have a custom version of a protocol with the same name, we prefer the custom version.
 */
const preferCustomProtocolVersions = (protocols: LensProtocol[]): LensProtocol[] => {
  const [custom, nonCustom] = R.partition(
    (p: LensProtocol) => p.fullPath.startsWith("custom-protocols"),
    protocols
  );

  const customProtocolNames = R.pluck("name", custom);

  return [
    ...custom,
    ...R.reject((p: LensProtocol) => customProtocolNames.includes(p.name), nonCustom)
  ];
};

/**
 * Fetches lens protocols that have data available for the given time range.
 * Returns an array of protocols with name (title from lens content) and fullPath.
 */
export const useLensProtocols = (params: UseLensProtocolsParams) => {
  const { tenantId } = useAuth();
  const { timeRange, permittedEmails } = params;

  return useQuery<LensProtocol[]>({
    enabled: !!tenantId,
    queryKey: ["lensProtocols", tenantId, timeRange, permittedEmails],
    queryFn: async () => {
      // Fetch distinct protocol names from the database (protocols with data)
      const distinctProtocols = await fetchDistinctProtocols({ timeRange, permittedEmails });

      if (distinctProtocols.length === 0) {
        return [];
      }
      // Fetch the consolidated lens list to get fullPaths
      const { consolidatedLensList } = await getLensData(tenantId!, "dummy");

      // For each distinct protocol, find its fullPath and extract the title
      const protocolPromises = consolidatedLensList.map(async (lensItem: ConsolidatedLensItem) => {

        // Download the lens content to extract the title
        let title = lensItem.filename;
        try {
          const content = await getLensContent(tenantId!, lensItem.fullPath);
          const extractedTitle = extractTitleFromMarkdown(content);
          if (extractedTitle) {
            title = stripTitleSuffixes(extractedTitle);
          }
        } catch (error) {
          console.warn(`Failed to fetch content for lens ${lensItem.fullPath}:`, error);
        }

        return {
          name: title,
          fullPath: lensItem.fullPath
        };
      });

      const protocols = await Promise.all(protocolPromises);
      const result = R.pipe(
        R.filter((p: LensProtocol) => distinctProtocols.includes(p.name)),
        preferCustomProtocolVersions,
        R.sortBy((p: LensProtocol) => p.name)
      )(protocols);
      return result;
    },
    staleTime: 5 * 60 * 1000
  });
};
